import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import isempty from 'lodash/isEmpty';

import { BookingsObject } from '../helpers/addressComponentHelper';
import { ApiClientsContext, EventType } from './ApiClientsContext';
import { ErrorContext } from './ErrorContext';
import { MemberContext } from './MemberContext';

export interface LocationState {
  CER?: BookingsObject;
  REC?: BookingsObject;
  WEDDING?: BookingsObject;
}

export interface LocationsSaved {
  CER: boolean;
  REC: boolean;
}

interface Default {
  location: LocationState;
  setCeremonyAddress: (address?: BookingsObject, sameAsCeremony?: boolean) => void;
  setReceptionAddress: (address?: BookingsObject) => void;
  setWeddingAddress: (address?: BookingsObject) => void;
  deleteBooking: (eventType?: EventType) => Promise<void>;
  locationsSaved: LocationsSaved;
  setLocationsSaved: (l: LocationsSaved) => void;
}

interface Props {
  children: ReactNode | ReactNode[];
}

export const LocationContext = createContext<Default>({} as Default);

export const LocationProvider: React.FC<Props> = ({ children }) => {
  const [location, setLocation] = useState<LocationState>({
    CER: undefined,
    REC: undefined,
    WEDDING: undefined,
  });
  const [locationsSaved, setLocationsSaved] = useState<LocationsSaved>({ CER: false, REC: false });
  const { member } = useContext(MemberContext);
  const { setErrors } = useContext(ErrorContext);
  const { weddingApi } = useContext(ApiClientsContext);

  const setCeremonyAddress = (address?: BookingsObject, sameAsCeremony: boolean = false): void => {
    setLocation(prev => ({
      ...prev,
      CER: address,
      REC: (sameAsCeremony && address) || prev.REC,
    }));
  };

  const setReceptionAddress = (address?: BookingsObject): void => {
    setLocation(prev => ({
      ...prev,
      REC: address,
    }));
  };

  const setWeddingAddress = (address?: BookingsObject): void => {
    setLocation(prev => ({
      ...prev,
      WEDDING: address,
    }));
  };

  const deleteBooking = (eventType?: EventType): Promise<void> => {
    return new Promise(resolve => {
      let bookingId;
      if (eventType === 'Ceremony') {
        bookingId = location.CER && location.CER.id;
        setCeremonyAddress(undefined);
        setLocationsSaved({ CER: false, REC: locationsSaved.REC });
      } else if (eventType === 'Reception') {
        bookingId = location.REC && location.REC.id;
        setReceptionAddress(undefined);
        setLocationsSaved({ CER: locationsSaved.CER, REC: false });
      }

      if (bookingId) {
        weddingApi.deleteBooking(bookingId).then(() => {
          return resolve();
        });
      } else {
        return resolve();
      }
    });
  };

  useEffect((): void => {
    const getBookings = async (): Promise<void> => {
      try {
        if (!member) return;
        const { bookings } = await weddingApi.getBookings(member.id);
        // @ts-ignore
        const ceremony = bookings.find(b => b.category_code === 'CER');
        // @ts-ignore
        const reception = bookings.find(b => b.category_code === 'REC');
        setCeremonyAddress(ceremony);
        setReceptionAddress(reception);

        if (!isempty(ceremony))
          setLocationsSaved({
            REC: locationsSaved.REC,
            CER: true,
          });

        if (!isempty(reception))
          setLocationsSaved({
            REC: true,
            CER: locationsSaved.CER,
          });
      } catch (error) {
        setErrors();
      }
    };
    getBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]);

  return (
    <LocationContext.Provider
      value={{
        location,
        setCeremonyAddress,
        setReceptionAddress,
        setWeddingAddress,
        deleteBooking,
        locationsSaved,
        setLocationsSaved,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};
